import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import {
  TSavedDemandNoteData,
} from "./level-2-application-approval.hooks";
import { level1ApplicationsKeyStore } from "./level-1-application-approval.hooks";

export type TCertificateData = Record<string, any>;

export type TLevel1PaymentReceipt = {
  receipt_id: number;
  receipt_no?: number;
  amount_paid: number;
  payment_date?: string;
  receipt_file?: string;
  application_id: string;
  payment_status:
  | "Draft"
  | "Payment Acknowledgment"
  | "Payment Discrepancy"
  | "Payment Successful";
};

export const level1PaymentKeyStore = {
  plotShapes: ["plot-shapes"],
  certificateData: (...args: (string | number | undefined)[]) => [
    "level-1-certificate-data",
    ...args,
  ],
  demandNoteData: (...args: (number | string | undefined)[]) => [
    "level-1-payment-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-1-payment-last-review", id],
  receipts: (id?: number | string) => ["level-1-payment-receipts", id],
};

export function useApplicationReceiptsForLevel1({
  id,
}: {
  id: number | string;
}) {
  return useQuery({
    queryKey: level1PaymentKeyStore.receipts(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TLevel1PaymentReceipt[];
      }>(`/level-1-payment-approval/receipts/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useLevel1UpdateReceiptStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      receiptId: number;
      status: "Payment Acknowledgment" | "Payment Discrepancy";
      applicationId: number;
    }) => _axios.patch("/level-1-payment-approval/receipts/update", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1PaymentKeyStore.receipts(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel1LastPaymentReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level1PaymentKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          application: {
            id: number;
            applicationId: string;
            status: string;
          };
          review: null | {
            comment_text?: string;
            id: number;
          };
        };
      }>(`/level-1-payment-approval/review/${id}`),
    select: (d) => d?.data?.data,
    staleTime: 10 * 60 * 1000,
  });
}

export function useLevel1SavePaymentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; comment: string }) =>
      _axios.post("/level-1-payment-approval/save-review", data),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level1PaymentKeyStore.lastReview(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong while saving comments!");
    },
    onSuccess: () => {
      SuccessToast("Saved review!");
    },
  });
}

export function useLevel1ApprovePaymentApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number }) =>
      _axios.post("/level-1-payment-approval/approve", data),
    onError: () => {
      ErrorToast("Something went wrong while approving application!");
    },
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Approved!");
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
  });
}

export function useLevel1ReturnPaymentApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number }) =>
      _axios.post("/level-1-payment-approval/return", data),
    onError: () => {
      ErrorToast("Something went wrong while returning application!");
    },
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Returned!");
      qc.invalidateQueries({
        queryKey: level1ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: level1ApplicationsKeyStore.stats() });
    },
  });
}

export function useLevel1DemandNoteDataForPayment({
  applicationId,
  reviewId,
}: {
  applicationId: number;
  reviewId?: number;
}) {
  return useQuery({
    queryKey: level1PaymentKeyStore.demandNoteData(applicationId),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TSavedDemandNoteData;
      }>(`/level-1-payment-approval/demand-note-data/${applicationId}`),
    select: (d) => d?.data?.data,
    enabled: !!applicationId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useLevel1SavedCertificateData({
  applicationId,
  reviewId,
}: {
  applicationId: number;
  reviewId?: number;
}) {
  return useQuery({
    queryKey: level1PaymentKeyStore.certificateData(applicationId, reviewId),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: Record<string, any>;
      }>(`/level-1-payment-approval/certificate-data`, {
        params: {
          applicationId,
          reviewId,
        },
      }),
    select: (d) => d?.data?.data,
    enabled: !!applicationId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useLevel1SaveCertificateData() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
    }) =>
      _axios.post<Blob>("/level-1-payment-approval/certificate-data", data, {
        responseType: "blob",
      }),
    onError: () =>
      ErrorToast("Something went wrong while saving sample demand note!"),
  });
}

export type TPlotShape = Record<string, any>;

export function useLevel1PlotShapes() {
  return useQuery({
    queryKey: level1PaymentKeyStore.plotShapes,
    queryFn: () =>
      _axios.get<{ success: true; data: TPlotShape[] }>(
        "/level-1-payment-approval/plot-shapes"
      ),
    staleTime: 10 * 60 * 1000,
    select: (result) => result?.data?.data,
  });
}
